import React from 'react';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  shadow?: 'none' | 'soft' | 'medium' | 'large' | 'glow';
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  border?: boolean;
  gradient?: boolean;
  glass?: boolean;
  onClick?: () => void;
}

const Card: React.FC<CardProps> = ({
  children,
  className = '',
  hover = false,
  padding = 'md',
  shadow = 'soft',
  rounded = 'xl',
  border = true,
  gradient = false,
  glass = false,
  onClick
}) => {
  const getPaddingClasses = () => {
    switch (padding) {
      case 'none':
        return '';
      case 'xs':
        return 'p-3';
      case 'sm':
        return 'p-4';
      case 'lg':
        return 'p-8';
      case 'xl':
        return 'p-10';
      default:
        return 'p-6';
    }
  };

  const getShadowClasses = () => {
    switch (shadow) {
      case 'none':
        return '';
      case 'soft':
        return 'shadow-soft';
      case 'medium':
        return 'shadow-medium';
      case 'large':
        return 'shadow-large';
      case 'glow':
        return 'shadow-glow';
      default:
        return 'shadow-soft';
    }
  };

  const getRoundedClasses = () => {
    switch (rounded) {
      case 'sm':
        return 'rounded-lg';
      case 'md':
        return 'rounded-xl';
      case 'lg':
        return 'rounded-2xl';
      case 'xl':
        return 'rounded-3xl';
      case '2xl':
        return 'rounded-4xl';
      case '3xl':
        return 'rounded-5xl';
      default:
        return 'rounded-xl';
    }
  };

  const getBackgroundClasses = () => {
    if (glass) {
      return 'bg-white/80 backdrop-blur-md';
    }
    if (gradient) {
      return 'bg-gradient-to-br from-white via-white to-neutral-50/50';
    }
    return 'bg-white';
  };

  const getBorderClasses = () => {
    if (!border) return '';
    if (glass) return 'border border-white/20';
    return 'border border-neutral-200/60';
  };

  const hoverClasses = hover 
    ? 'hover:shadow-large hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out cursor-pointer group' 
    : 'transition-all duration-200 ease-out';

  const interactiveClasses = onClick 
    ? 'cursor-pointer active:scale-[0.98] transition-transform duration-150' 
    : '';

  return (
    <div
      className={`
        ${getBackgroundClasses()}
        ${getRoundedClasses()}
        ${getBorderClasses()}
        ${getShadowClasses()}
        ${getPaddingClasses()}
        ${hoverClasses}
        ${interactiveClasses}
        relative overflow-hidden
        ${className}
      `.replace(/\s+/g, ' ').trim()}
      onClick={onClick}
    >
      {/* Subtle gradient overlay for enhanced depth */}
      {!glass && (
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-neutral-50/30 pointer-events-none" />
      )}
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>

      {/* Hover glow effect */}
      {hover && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-transparent to-secondary-500/5 rounded-inherit" />
        </div>
      )}
    </div>
  );
};

export default Card;